import { Group, Stack, Stepper, Text } from "@mantine/core"
import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import {
  AgecCertificateDetails,
  AGECResponses,
} from "@tfg/types/endpoints/agec"
import { Configurations } from "@tfg/types/schemas/communication-studio/configurationParameters"
import { textColorCssVariable } from "@tfg/types/schemas/communication-studio/cssVariables"
import { FlagComponent } from "country-flag-icons/react/3x2"

import styles from "../ActivityList.module.css"
import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"
import { ActivityCertificates } from "../ui/ActivityCertificates/ActivityCertificates"
import { CountryLabel } from "../ui/CountryLabel"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"
import { getActivities } from "../utils/utils"

import { AGECDataPending } from "./AGECDataPending"
import { ActivityIcon } from "./ActivityIcon"

type Activity = {
  activity: {
    name: string
  }
  country:
    | {
        name: string | undefined
        flag: FlagComponent | undefined
      }
    | undefined
  certificates: AgecCertificateDetails[]
}

type Props = {
  agecData: AGECResponses.GenericAgecData
  certificateFilterFunction?: (certificate: AgecCertificateDetails) => boolean
} & Configurations[typeof CUSTOMISATION_CATEGORIES.ACTIVITIES] & {
    standardsToBeShown?: number[]
  }
const ICON_SIZE = 48
const hasAtLeastOneCountry = (activities: Activity[] | null): boolean => {
  if (!activities) return false
  return activities.some((activity) => activity.country?.name !== undefined)
}

const getStepperStyles = (showIcons: boolean, isLast: boolean) => ({
  ...(showIcons && {
    styles: {
      stepIcon: {
        minWidth: ICON_SIZE * 2 + 14,
        height: ICON_SIZE * 2 + 14,
      },
      verticalSeparator: {
        minHeight: ICON_SIZE * 2 + 14,
        top: ICON_SIZE + 20,
      },
    },
  }),
  ...(isLast && {
    styles: {
      step: { height: "unset", minHeight: "unset" },
      stepIcon: { height: "unset", minHeight: "unset" },
    },
  }),
})

const ActivityLabel = ({ activityName }: { activityName: string }) => {
  const { t } = useGetTranslationsContext()

  return (
    <Group
      className={CommunicationStudioClasses.ACTIVITY}
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
      style={{
        wordBreak: "break-word",
      }}
    >
      {t(`activities:${activityName}`)}
    </Group>
  )
}

const ActivityLocationLabel = ({
  country,
  showCountryFlags,
}: {
  country: Activity["country"]
  showCountryFlags: boolean
}) => {
  const { t } = useGetTranslationsContext()
  if (!country?.name) {
    return (
      <Text
        c={DesignStudioParametersToolkit.getCssVariableLiteralName(
          textColorCssVariable
        )}
      >
        {t("loading.pendingLocation")}
      </Text>
    )
  }

  return showCountryFlags ? (
    <CountryLabel country={country.name} flag={country.flag} />
  ) : (
    <Text
      className={CommunicationStudioClasses.COUNTRY}
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
    >
      {country.name}
    </Text>
  )
}

export function ActivityList({
  agecData,
  showActivityIcons,
  showCountryFlags,
  showCertificates,
  activitiesDepth = 3,
  standardsToBeShown,
}: Props) {
  const activities = getActivities(agecData, standardsToBeShown ?? []).filter(
    (a): a is NonNullable<typeof a> => a !== null
  )
  const visibleActivities = activities.slice(0, activitiesDepth)
  return (
    <Stack
      py="sm"
      justify="center"
      align="center"
      c={DesignStudioParametersToolkit.getCssVariableLiteralName(
        textColorCssVariable
      )}
    >
      <Stack w="100%" align="center" justify="center">
        {hasAtLeastOneCountry(activities) ? (
          <Stepper
            orientation="vertical"
            classNames={styles}
            active={0}
            styles={{
              verticalSeparator: {
                borderColor:
                  DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textColorCssVariable
                  ),
              },
            }}
          >
            {visibleActivities.map((op, i) => (
              <Stepper.Step
                key={`op-${i}`}
                {...getStepperStyles(
                  showActivityIcons ?? false,
                  i === visibleActivities.length - 1
                )}
                icon={
                  showActivityIcons ? (
                    <ActivityIcon activityName={op.activity.name} />
                  ) : (
                    <ActivityLabel activityName={op.activity.name} />
                  )
                }
                label={
                  !showActivityIcons ? (
                    <ActivityLocationLabel
                      country={op.country}
                      showCountryFlags={showCountryFlags}
                    />
                  ) : null
                }
                description={
                  <>
                    {showActivityIcons ? (
                      <Stack
                        mih={ICON_SIZE * 2}
                        gap="xs"
                        justify="start"
                        mt={ICON_SIZE / 2}
                      >
                        <ActivityLocationLabel
                          country={op.country}
                          showCountryFlags={showCountryFlags}
                        />
                        {showCertificates && (
                          <ActivityCertificates
                            certificates={op.certificates}
                          />
                        )}
                      </Stack>
                    ) : (
                      showCertificates && (
                        <ActivityCertificates certificates={op.certificates} />
                      )
                    )}
                  </>
                }
              />
            ))}
          </Stepper>
        ) : (
          <AGECDataPending />
        )}
      </Stack>
    </Stack>
  )
}
